/**
 * OnPoint BI. Canonical number, currency, and percent formatting.
 *
 * Backed by Section 6 of `/app/memory/project-brief-v1.md`:
 *   1. US currency: `$1,234.56` (comma thousands, leading `$`, exactly two decimals).
 *   2. Negative sign convention:
 *        mode "kpi"  (default). Minus sign prefix.   e.g. -$1,234.56
 *        mode "table"                                e.g. ($1,234.56)
 *        mode "export" (CSV / XLSX / PDF accounting) e.g. ($1,234.56)
 *   3. Large number abbreviation:
 *        Thousands  $12.3K
 *        Millions   $4.5M
 *        Billions   $2.1B
 *        Trillions  $1.2T
 *      The full precise value must always remain retrievable via `formatFullCurrency`
 *      (that is what MetricValue puts in its hover tooltip).
 *   4. Exports never abbreviate.
 *   5. Locale scope. US only.
 *
 * These functions are the single source of truth. Any legacy `Intl.NumberFormat`
 * or `toLocaleString` call in the codebase should migrate to these helpers.
 */

const nfUsdFull = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const nfUsdNoDecimals = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const nfPlainInt = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Convert any input to a finite Number, or null.
 * Accepts number, numeric string, "$1,234.56", null, undefined.
 */
export function toNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const cleaned = v.replace(/[,$\s]/g, "").replace(/^\((.*)\)$/, "-$1");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Full precise currency, always `$1,234.56` with two decimals.
 * Negative uses parentheses in "table" and "export" modes, minus sign in "kpi".
 *
 * Use this for hover tooltips, table cells, and any export.
 */
export function formatFullCurrency(value, { mode = "kpi", decimals = 2 } = {}) {
  const n = toNumber(value);
  if (n === null) return "--";
  const fmt = decimals === 0 ? nfUsdNoDecimals : nfUsdFull;
  const abs = Math.abs(n);
  const body = fmt.format(abs);
  if (n >= 0) return body;
  if (mode === "table" || mode === "export") return `(${body})`;
  return `-${body}`;
}

/**
 * Abbreviated currency for constrained space. K / M / B / T.
 * Threshold at 10,000 so anything below stays as full `$1,234.56` for accuracy.
 *
 * Negatives follow the same mode rule as `formatFullCurrency`.
 */
export function formatCompactCurrency(value, { mode = "kpi", decimalsBelowK = 0 } = {}) {
  const n = toNumber(value);
  if (n === null) return "--";
  const abs = Math.abs(n);
  const sign = n < 0;

  let body;
  if (abs >= 1e12) body = `$${(abs / 1e12).toFixed(1)}T`;
  else if (abs >= 1e9) body = `$${(abs / 1e9).toFixed(1)}B`;
  else if (abs >= 1e6) body = `$${(abs / 1e6).toFixed(1)}M`;
  else if (abs >= 10000) body = `$${(abs / 1000).toFixed(1)}K`;
  else if (decimalsBelowK === 0) body = nfUsdNoDecimals.format(abs);
  else body = nfUsdFull.format(abs);

  if (!sign) return body;
  if (mode === "table" || mode === "export") return `(${body})`;
  return `-${body}`;
}

/**
 * Auto-decide: compact when the full number would exceed `charBudget`, else full.
 * Default budget is 12 chars (fits a typical KPI card at 375px width).
 *
 * Returns { display, full, wasAbbreviated } so a MetricValue component can bind
 * the compact display to the hover tooltip full precise value.
 */
export function formatSmartCurrency(value, { mode = "kpi", charBudget = 12, decimals = 2 } = {}) {
  const n = toNumber(value);
  if (n === null) return { display: "--", full: "--", wasAbbreviated: false };
  const full = formatFullCurrency(n, { mode, decimals });
  if (full.length <= charBudget) {
    return { display: full, full, wasAbbreviated: false };
  }
  const compact = formatCompactCurrency(n, { mode });
  return { display: compact, full, wasAbbreviated: true };
}

/**
 * Plain integer formatting (no currency symbol), for counts and non-money numeric labels.
 * `1234567` becomes `1,234,567`.
 */
export function formatInteger(value) {
  const n = toNumber(value);
  if (n === null) return "--";
  return nfPlainInt.format(n);
}

/**
 * Percent with configurable decimals. `12.345` becomes `12.3%`.
 * Accepts either a fraction (0.123 with `fromFraction: true`) or a percentage number (12.3).
 */
export function formatPercent(value, { decimals = 1, fromFraction = false } = {}) {
  const n = toNumber(value);
  if (n === null) return "--";
  const pct = fromFraction ? n * 100 : n;
  return `${pct.toFixed(decimals)}%`;
}

/**
 * Signed delta with an arrow glyph. Used on KPI deltas ("MoM Growth", "YoY Growth").
 * `up` and `down` glyphs are `\u25B2` and `\u25BC`. Never emoji, never em dash.
 * Returns a plain string.
 */
export function formatSignedPercent(value, { decimals = 1, fromFraction = false } = {}) {
  const n = toNumber(value);
  if (n === null) return "--";
  const pct = fromFraction ? n * 100 : n;
  const glyph = pct >= 0 ? "\u25B2" : "\u25BC";
  return `${glyph} ${Math.abs(pct).toFixed(decimals)}%`;
}

/**
 * Sentinel for empty / null / N.A. cells. Two hyphens, matches finance conventions
 * while complying with the no-em-dash rule (Section 0.4 of the brief).
 */
export const EMPTY_DISPLAY = "--";
